import { ErrorHandler } from '@angular/core';

/**
 * Stale-chunk handling.
 *
 * After a deployment, the hashed lazy-chunk filenames change. A browser still
 * running the previous build will try to lazy-load a chunk that no longer
 * exists on the server, producing errors like:
 *   - "Failed to fetch dynamically imported module"
 *   - "error loading dynamically imported module"
 *   - "Loading chunk X failed"
 *
 * The fix is a one-time hard reload, which fetches the fresh index.html and the
 * new chunk names. A sessionStorage guard prevents an infinite reload loop if
 * the chunk is genuinely missing for some other reason.
 */

const CHUNK_ERROR_REGEX =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Loading chunk [^\s]+ failed|Importing a module script failed/i;

const RELOAD_FLAG = 'chunk-reload-attempted';

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const message = String(
    (error as any)?.message ??
      (error as any)?.reason?.message ??
      (error as any)?.reason ??
      error,
  );
  return CHUNK_ERROR_REGEX.test(message);
}

/**
 * Reloads the page once per session when a stale-chunk error is detected.
 * Returns true if it handled (triggered a reload), false otherwise.
 */
export function handleChunkLoadError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;

  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) {
      // Already reloaded once this session; don't loop.
      return false;
    }
    sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch {
    // sessionStorage unavailable (private mode, etc.) — reload anyway.
  }

  window.location.reload();
  return true;
}

/** Clear the guard after a successful boot so future deploys can reload again. */
export function clearChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* ignore */
  }
}

/**
 * Wraps an existing ErrorHandler (e.g. Sentry's) so stale-chunk errors trigger
 * a reload. Non-chunk errors are delegated to the wrapped handler unchanged.
 */
export class ChunkLoadErrorHandler implements ErrorHandler {
  constructor(private readonly delegate: ErrorHandler) {}

  handleError(error: unknown): void {
    if (handleChunkLoadError(error)) {
      return;
    }
    this.delegate.handleError(error);
  }
}
